import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  QUOTA_EXCEEDED_EVENT,
  type QuotaExceededDetail,
} from "../lib/http/quotaEvents";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

/**
 * Listens for global quota-exceeded events (emitted by the axios interceptor and
 * the RAG streaming error path) and shows a single upgrade-prompt modal that
 * links to the pricing page.
 */
const UpgradePromptProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<QuotaExceededDetail | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<QuotaExceededDetail>).detail;
      if (detail) setPrompt(detail);
    };
    window.addEventListener(QUOTA_EXCEEDED_EVENT, handler);
    return () => window.removeEventListener(QUOTA_EXCEEDED_EVENT, handler);
  }, []);

  const close = () => setPrompt(null);

  useBodyScrollLock(!!prompt);

  return (
    <>
      {children}
      {prompt && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          onClick={close}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-xl text-white shadow-glow">
              ⚡
            </div>
            <h2 className="text-center text-lg font-semibold text-gray-900">
              You've hit your limit
            </h2>
            <p className="mt-2 text-center text-sm text-gray-500">{prompt.message}</p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => {
                  close();
                  navigate("/pricing");
                }}
                className="btn-primary btn-lg w-full"
              >
                View plans
              </button>
              <button
                type="button"
                onClick={close}
                className="btn-ghost btn-lg w-full"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpgradePromptProvider;
